import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, Cpu, MemoryStick, HardDrive, Gauge, Network, RefreshCw,
  TrendingUp, TrendingDown, Activity, AlertCircle, Clock
} from 'lucide-react';
import { aiAgent } from '../../../services/api/client';
import { toast } from '../../../utils/toastUtils';

const AIAgentMetrics = () => {
  const [metrics, setMetrics] = useState(null);
  const [currentMetrics, setCurrentMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('1h');
  const [resolution, setResolution] = useState('1m');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [metricsData, currentData] = await Promise.all([
        aiAgent.getMetrics(period, resolution),
        aiAgent.getCurrentMetrics()
      ]);
      setMetrics(metricsData);
      setCurrentMetrics(currentData);
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
      toast.error('Erreur lors du chargement des metriques');
    } finally {
      setLoading(false);
    }
  }, [period, resolution]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Simple SVG line chart
  const MiniChart = ({ data, color, height = 60 }) => {
    if (!data || data.length === 0) {
      return (
        <div className="h-16 flex items-center justify-center text-gray-500 text-sm">
          Pas de donnees
        </div>
      );
    }

    const values = data.map(d => d.value || 0);
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const range = max - min || 1;

    const width = 300;
    const points = values.map((v, i) => {
      const x = (i / (values.length - 1 || 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x},${y}`;
    }).join(' ');

    // Create fill path
    const fillPath = `M0,${height} L${points} L${width},${height} Z`;

    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: `${height}px` }}>
        {/* Fill area */}
        <path d={fillPath} fill={`${color}20`} />
        {/* Line */}
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Current value dot */}
        {values.length > 0 && (
          <circle
            cx={width}
            cy={height - ((values[values.length - 1] - min) / range) * height}
            r="4"
            fill={color}
          />
        )}
      </svg>
    );
  };

  // Metric card with chart
  const MetricCard = ({ title, icon: Icon, data, current, unit = '%', color, iconColor }) => {
    const trend = data && data.length >= 2
      ? data[data.length - 1]?.value - data[data.length - 2]?.value
      : 0;

    return (
      <div className="bg-brand-black rounded-xl p-4 border border-gray-800">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${iconColor}`} />
            <span className="text-gray-400 font-medium">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-white">
              {current?.toFixed(1) || 0}{unit}
            </span>
            {trend !== 0 && (
              <span className={`flex items-center text-xs ${trend > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              </span>
            )}
          </div>
        </div>
        <MiniChart data={data} color={color} />
        {data && data.length > 0 && (
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>Min: {Math.min(...data.map(d => d.value || 0)).toFixed(1)}{unit}</span>
            <span>Max: {Math.max(...data.map(d => d.value || 0)).toFixed(1)}{unit}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-brand-green" />
            Métriques Système
          </h3>
          <p className="text-gray-400 text-sm mt-1">Évolution des ressources dans le temps</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-1.5 bg-brand-black-lighter border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="1h">1 heure</option>
            <option value="6h">6 heures</option>
            <option value="24h">24 heures</option>
            <option value="7d">7 jours</option>
          </select>
          <select
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            className="px-3 py-1.5 bg-brand-black-lighter border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="1m">1 min</option>
            <option value="5m">5 min</option>
            <option value="15m">15 min</option>
            <option value="1h">1 heure</option>
          </select>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 bg-brand-black-lighter rounded-lg text-gray-400 hover:text-brand-green"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && !metrics ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green"></div>
        </div>
      ) : (
        <>
          {/* Main Metrics Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MetricCard
              title="CPU"
              icon={Cpu}
              data={metrics?.cpu}
              current={currentMetrics?.cpu_percent}
              color="#10b981"
              iconColor="text-green-400"
            />
            <MetricCard
              title="Memoire RAM"
              icon={MemoryStick}
              data={metrics?.memory}
              current={currentMetrics?.memory_percent}
              color="#3b82f6"
              iconColor="text-blue-400"
            />
            <MetricCard
              title="GPU"
              icon={Gauge}
              data={metrics?.gpu}
              current={currentMetrics?.gpu_utilization}
              color="#a855f7"
              iconColor="text-purple-400"
            />
            <MetricCard
              title="Disque"
              icon={HardDrive}
              data={metrics?.disk}
              current={currentMetrics?.disk_percent}
              color="#f97316"
              iconColor="text-orange-400"
            />
          </div>

          {/* Activity Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MetricCard
              title="Scans Actifs"
              icon={Activity}
              data={metrics?.active_scans}
              current={currentMetrics?.active_scans}
              unit=""
              color="#06b6d4"
              iconColor="text-cyan-400"
            />
            <MetricCard
              title="Erreurs"
              icon={AlertCircle}
              data={metrics?.errors}
              current={currentMetrics?.error_count_1h}
              unit=""
              color="#ef4444"
              iconColor="text-red-400"
            />
          </div>

          {/* Additional Current Stats */}
          {currentMetrics && (
            <div className="bg-brand-black rounded-xl p-6 border border-gray-800">
              <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-brand-green" />
                Statistiques Actuelles
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="text-center p-3 bg-brand-black-lighter rounded-lg">
                  <p className="text-gray-500 text-xs">RAM Utilisee</p>
                  <p className="text-lg font-bold text-blue-400">
                    {currentMetrics.memory_used_gb?.toFixed(1) || 0} GB
                  </p>
                </div>
                <div className="text-center p-3 bg-brand-black-lighter rounded-lg">
                  <p className="text-gray-500 text-xs">Swap</p>
                  <p className="text-lg font-bold text-yellow-400">
                    {currentMetrics.swap_percent?.toFixed(1) || 0}%
                  </p>
                </div>
                <div className="text-center p-3 bg-brand-black-lighter rounded-lg">
                  <p className="text-gray-500 text-xs">Mém. GPU</p>
                  <p className="text-lg font-bold text-purple-400">
                    {currentMetrics.gpu_memory_percent?.toFixed(1) || 0}%
                  </p>
                </div>
                <div className="text-center p-3 bg-brand-black-lighter rounded-lg">
                  <p className="text-gray-500 text-xs">Temp. GPU</p>
                  <p className={`text-lg font-bold ${(currentMetrics.gpu_temperature || 0) > 80 ? 'text-red-400' : 'text-green-400'}`}>
                    {currentMetrics.gpu_temperature?.toFixed(0) || 0}C
                  </p>
                </div>
                <div className="text-center p-3 bg-brand-black-lighter rounded-lg">
                  <p className="text-gray-500 text-xs">Disque Libre</p>
                  <p className="text-lg font-bold text-orange-400">
                    {currentMetrics.disk_free_gb?.toFixed(0) || 0} GB
                  </p>
                </div>
                <div className="text-center p-3 bg-brand-black-lighter rounded-lg">
                  <p className="text-gray-500 text-xs">Services OK</p>
                  <p className="text-lg font-bold text-green-400">
                    {currentMetrics.services_healthy || 0}/{currentMetrics.services_total || 0}
                  </p>
                </div>
              </div>

              {/* Network Stats */}
              {(currentMetrics.network_bytes_sent || currentMetrics.network_bytes_recv) && (
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <div className="flex items-center gap-2 mb-3">
                    <Network className="w-4 h-4 text-cyan-400" />
                    <span className="text-gray-400 text-sm">Réseau</span>
                  </div>
                  <div className="flex gap-6">
                    <div>
                      <span className="text-gray-500 text-xs">Envoyé : </span>
                      <span className="text-white">
                        {formatBytes(currentMetrics.network_bytes_sent)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs">Reçu : </span>
                      <span className="text-white">
                        {formatBytes(currentMetrics.network_bytes_recv)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Helper to format bytes
const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default AIAgentMetrics;
